/**
 * AC6 (Notion 3bfc94b6-2cc7-81cf-b780-f58ca1259319): noise filters active
 * BEFORE prod is ever turned on — extensions, bots/crawlers, the
 * `ResizeObserver loop` false-positive, per-deploy chunk-load errors, and
 * foreign domains. Shared by the browser and server init (the server side
 * only ever sees its own subset, but reusing one list avoids two lists
 * drifting apart).
 *
 * These are Sentry's own documented recipes
 * (docs.sentry.io "Dealing with noisy errors") plus two specific to this
 * repo: chunk-load errors are the exact 47% of the 758 5xx-to-browser
 * Cloud Logging hits the De-risk found (name-hashed chunk files 404ing
 * across a deploy — expected, not actionable per-occurrence), and
 * `sourcemap:move`'s directory layout confirms hashed chunk filenames are
 * how this build ships.
 */
export const IGNORE_ERROR_PATTERNS: (string | RegExp)[] = [
  // Browser extensions injecting into the page — not Vaki's code path.
  'top.GLOBALS',
  /^ResizeObserver loop/,
  'ResizeObserver loop completed with undelivered notifications',
  'ResizeObserver loop limit exceeded',
  // Safari/older browsers' benign "we cancelled this in-flight request"
  // noise, not a real failure.
  'Non-Error promise rejection captured',
  // Chunk load failures across a deploy: a browser tab open across a
  // release requests a hashed chunk filename that no longer exists on the
  // new revision. Expected during every deploy, not actionable per-event
  // — AC6 asks these filtered from day 0, not silently ignored forever;
  // see the day-7 signal/noise report requirement in the runbook.
  /Loading chunk [\d]+ failed/,
  /ChunkLoadError/,
  /Failed to fetch dynamically imported module/,
  /Importing a module script failed/,
  // Network noise indistinguishable from a real bug without more context
  // than the browser gives us.
  'Network request failed',
  'Load failed',
  'NetworkError when attempting to fetch resource'
];

/**
 * Errors whose stack frames resolve ONLY to these origins are dropped —
 * i.e. the crash happened in code we don't ship (an extension, an
 * injected script), not in vaki-web. Sentry's `denyUrls` matches against
 * the top stack frame's URL.
 */
export const DENY_URLS: RegExp[] = [
  // Chrome/Firefox/Safari extension schemes.
  /^chrome-extension:\/\//,
  /^moz-extension:\/\//,
  /^safari-extension:\/\//,
  /^safari-web-extension:\/\//,
  // Common ad-blocker / injected-script origins seen across the industry
  // recipe list — not Vaki's code, never fixable from this repo.
  /graph\.facebook\.com/,
  /connect\.facebook\.net/
];

/**
 * User agents that never represent a donor's browser — filtered in
 * `beforeSend` since Sentry has no built-in bot filter for a self-hosted
 * check like this. Bounded, not exhaustive: unknown/new bots simply won't
 * match, which is an acceptable miss for a Slack-alert use case (a
 * handful of bot-origin errors reaching Sentry is noise, not a PII or
 * cost risk on its own) — unlike the PII scrubber, this list does NOT
 * need to fail closed.
 */
const BOT_USER_AGENT_PATTERN =
  /bot|crawler|spider|crawling|slurp|bingpreview|facebookexternalhit|headlesschrome|lighthouse|pagespeed|gtmetrix/i;

export function isBotUserAgent(userAgent: string | undefined): boolean {
  if (!userAgent) return false;
  return BOT_USER_AGENT_PATTERN.test(userAgent);
}
